"use client";

import {
	Bone,
	Col as DSCol,
	Grid,
	Skeleton,
	Text,
	type ColProps
} from "@pamperedchef/design-system";
import { type FC, type ReactNode } from "react";

import type { Locale } from "@/domain/catalog/product";

import { useListingProducts } from "../useListingProducts";
import { useListingRatings } from "../useListingRatings";
import { PlpProductGridItem } from "./PlpProductGridItem";
import * as styles from "./PlpProductGrid.module.scss";

// `ColProps` picks up `React.HTMLAttributes` from a `framer-motion` import in
// the DS types, so `className` is missing from it — add it back on the cast.
const Col = DSCol as FC<
	ColProps & { children?: ReactNode; className?: string }
>;

const CARD_SPAN = { size: 6, sm: 6, md: 4 } as const;

export interface PlpProductGridProps {
	locale: Locale;
	skeletonCount?: number;
	emptyMessage?: string;
}

export function PlpProductGrid({
	locale,
	skeletonCount = 9,
	emptyMessage = "No products match this category yet."
}: PlpProductGridProps): JSX.Element {
	const { products, isInitialLoad } = useListingProducts(locale);
	const ratings = useListingRatings(products.map((product) => product.sku));

	if (isInitialLoad) {
		return <PlpProductGridSkeleton count={skeletonCount} />;
	}

	if (products.length === 0) {
		return (
			<Text tag="p" styledAs="mdr" className={styles["grid__empty"]}>
				{emptyMessage}
			</Text>
		);
	}

	return (
		<Grid
			columns={12}
			gap="lg"
			className={styles["grid"]}
			data-component="PlpProductGrid"
		>
			{products.map((product) => (
				<Col
					key={product.sku}
					{...CARD_SPAN}
					className={styles["grid__cell"]}
				>
					<PlpProductGridItem
						product={product}
						rating={ratings.get(product.sku)}
					/>
				</Col>
			))}
		</Grid>
	);
}

interface PlpProductGridSkeletonProps {
	count: number;
}

function PlpProductGridSkeleton({
	count
}: PlpProductGridSkeletonProps): JSX.Element {
	return (
		<Grid columns={12} gap="lg" data-component="PlpProductGridSkeleton">
			{Array.from({ length: count }, (_, i) => (
				<Col key={i} {...CARD_SPAN}>
					<Skeleton>
						<Bone width="100%" height="200px" borderRadius="8px" />
					</Skeleton>
				</Col>
			))}
		</Grid>
	);
}
